import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Workflow } from "lucide-react";
import { errorMessage } from "@web/lib/api";
import { dripApi, dripSequencesQuery, type DripSequence } from "@web/lib/queries-automation";
import { useMe } from "@web/lib/session";
import {
  BottomSheet, Button, Card, EmptyState, Inline, Page, Region, Row, RowGroup, Skeleton, Stack,
  StatusChip, Text, Toggle, useToast, Workspace,
} from "@web/ui";
import { AdminsOnly } from "./Settings";

/** Settings › Drip sequences (SPEC §6.1 item 13, §7.10). The steps are read-only:
 * this cadence is the one the venue's own sales history proved. What staff need
 * here is the switch, and to be told plainly what turning it on will do. */
const channelLabel = (channel: string) => channel === "both" ? "Email and text" : channel === "sms" ? "Text" : "Email";
const dayLabel = (days: number) => days === 0 ? "Same day" : days === 1 ? "After 1 day" : `After ${days} days`;

export function SettingsDripSequences() {
  const me = useMe();
  const navigate = useNavigate();
  const toast = useToast();
  const sequences = useQuery({ ...dripSequencesQuery, enabled: me.companyAdmin });
  const [turningOn, setTurningOn] = useState<DripSequence | null>(null);
  const [busy, setBusy] = useState(false);
  if (!me.companyAdmin) return <AdminsOnly />;

  async function setActive(sequence: DripSequence, active: boolean) {
    setBusy(true);
    try {
      await dripApi.setActive(sequence.key, active);
      toast.success(active ? `${sequence.name} is on` : `${sequence.name} is off`);
    } catch (error) { toast.error(errorMessage(error)); }
    finally { setBusy(false); setTurningOn(null); }
  }

  return <Page>
    <Workspace variant="form"><Region area="main"><Stack gap={4}>
      <Text variant="callout" tone="secondary">
        A sequence writes to a lead over weeks or months. Nothing goes out without a person releasing it unless auto-send is on.
      </Text>
      {sequences.isError
        ? <EmptyState icon={Workflow} title="Sequences could not be loaded" body={errorMessage(sequences.error)}
            action={<Button onClick={() => void sequences.refetch()}>Try again</Button>} />
        : !sequences.data ? <Skeleton variant="cards" count={3} />
        : sequences.data.length === 0 ? <EmptyState icon={Workflow} title="No sequences" body="Seeded sequences appear here." />
          : <Stack gap={3}>{sequences.data.map(sequence => <Card key={sequence.id}><Stack gap={3}>
            <Inline justify="between" align="start" wrap>
              <Stack gap={1}>
                <Text variant="title">{sequence.name}</Text>
                <Text variant="caption" tone="secondary">
                  {sequence.steps.length} {sequence.steps.length === 1 ? "message" : "messages"}
                  {sequence.nextSequenceKey ? ` · then hands over to ${sequence.nextSequenceKey.replaceAll("_", " ")}` : " · then stops"}
                </Text>
              </Stack>
              <Stack gap={1} align="end">
                <StatusChip tone={sequence.active ? "success" : "neutral"}>{sequence.active ? "On" : "Off"}</StatusChip>
                {sequence.enrolled.active + sequence.enrolled.paused > 0 && <StatusChip tone="neutral">
                  {sequence.enrolled.active} active{sequence.enrolled.paused ? ` · ${sequence.enrolled.paused} paused` : ""}
                </StatusChip>}
              </Stack>
            </Inline>
            <Toggle label={`Run ${sequence.name}`} checked={sequence.active} disabled={busy}
              onChange={value => value ? setTurningOn(sequence) : void setActive(sequence, false)} />
            <RowGroup>
              {sequence.steps.map(step => <Row key={step.step}
                title={step.templateName ?? step.templateKey}
                subtitle={`${dayLabel(step.afterDays)} · ${channelLabel(step.channel)} · ${step.templateKey}`}
                wrap
                onClick={() => navigate({ to: "/settings/message-templates" })} />)}
            </RowGroup>
            <Text variant="caption" tone="tertiary">Timing is measured from when the lead was enrolled. Edit the words under Message templates.</Text>
          </Stack></Card>)}</Stack>}
    </Stack></Region></Workspace>

    <BottomSheet open={Boolean(turningOn)} onClose={() => setTurningOn(null)} title={`Turn on ${turningOn?.name ?? ""}?`}
      footer={<Button loading={busy} onClick={() => turningOn && void setActive(turningOn, true)}>Turn it on</Button>}>
      <Stack gap={3}>
        <Text>New leads will be enrolled from now on, and {turningOn?.steps.length ?? 0} messages will be written for each of them over time.</Text>
        <Text tone="secondary">A sequence stops by itself when the lead replies, books, is marked lost or asks not to be contacted. A message from a person pauses it for three days.</Text>
        <Text tone="secondary">Leads already in the system are not enrolled retroactively.</Text>
      </Stack>
    </BottomSheet>
  </Page>;
}
